import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getDetentionFreeHours, updateDetentionFreeHours } from '../../api/endpoints/admin';
import { actionFailureMessage } from '../../api/actionFailure';
import { staticQueryPolicy } from '../../api/queryPolicies';
import { Button } from '../../components/ui/Button';
import { useMutationHook } from '../../hooks/useMutationHook';
import { parseFreeHours } from './opsSettingsValidation';
import styles from './SettingsView.module.css';
import team from './OpsTeamTab.module.css';

const freeHoursKey = ['admin', 'settings', 'detention-free-hours'];

/**
 * Detention free time per location (rhino-api#378 C1, R52): whole hours 0–168, default 24.
 * Everyone reads it; only ops managers save (the API answers 403 otherwise).
 */
export function DetentionFreeHoursField({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: freeHoursKey,
    queryFn: getDetentionFreeHours,
    ...staticQueryPolicy,
  });
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (data != null) setValue(String(data));
  }, [data]);

  const save = useMutationHook((hours: number) => updateDetentionFreeHours(hours), {
    successMessage: t('stops.settings.saved'),
    invalidateKeys: [freeHoursKey],
    errorMessage: actionFailureMessage,
    onError: (err) => setError(actionFailureMessage(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseFreeHours(value);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setError(null);
    save.mutate(parsed.value);
  };

  return (
    <form className={team.field} onSubmit={submit} noValidate>
      <label className={styles.label} htmlFor="detentionFreeHours">
        {t('stops.settings.freeHoursLabel')}
      </label>
      <div className={team.inline}>
        <input
          id="detentionFreeHours"
          className={team.input}
          inputMode="numeric"
          value={isLoading ? '' : value}
          disabled={!canEdit || isLoading || save.isLoading}
          onChange={(e) => setValue(e.target.value)}
        />
        {canEdit && (
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            disabled={save.isLoading || isLoading}
          >
            {save.isLoading ? t('stops.sheet.saving') : t('common.actions.save')}
          </Button>
        )}
      </div>
      <span className={styles.cardHint}>{t('stops.settings.freeHoursHint')}</span>
      {isError && <span className={team.fieldError}>{t('stops.settings.loadError')}</span>}
      {error && (
        <span className={team.fieldError} role="alert">
          {error}
        </span>
      )}
    </form>
  );
}
